import { checkLDFlags } from '@/plugins/launchdarkly';
import { i18n } from '@/plugins/i18n';
import { queryClient } from '@/plugins/vueQuery';
import { useAgentBulkUploadStore } from '@/stores/agentBulkUploadStore';
import { FeatureFlags } from '@/types/featureFlags';
import type { AgentsEvent, EventAgentsBulkCreateComplete, EventAgentsBulkCreateFailed } from '@/types/websocket';
import { handleError } from '@/utils/errorHandling';
import { getAgentsQueryKey } from '@/utils/queries/agents';
import { addToast } from '@teamworksdev/vuetify';

const { t } = i18n.global;

const buildSummary = (data: EventAgentsBulkCreateComplete['data']): string => {
  const counts = [
    {
      count: data.providers_created,
      key: 'toast.agentBulkUploadComplete.counts.created',
    },
    {
      count: data.providers_updated,
      key: 'toast.agentBulkUploadComplete.counts.overwritten',
    },
    {
      count: data.associations_created,
      key: 'toast.agentBulkUploadComplete.counts.linked',
    },
    {
      count: data.associations_skipped,
      key: 'toast.agentBulkUploadComplete.counts.skipped',
    },
  ];

  return counts
    .filter(({ count }) => count > 0)
    .map(({ count, key }) => t(key, { count }))
    .join(', ');
};

export const handleAgentBulkCreateComplete = async (event: EventAgentsBulkCreateComplete): Promise<void> => {
  try {
    const enabled = await checkLDFlags([FeatureFlags.AGENT_CSV_UPLOAD_ENABLED]);
    if (!enabled) return;

    const failedCount = event.data.failed?.length ?? 0;
    const total = event.data.total ?? 0;
    const summary = buildSummary(event.data);

    const store = useAgentBulkUploadStore();
    store.applyFailures(event.data.failed ?? []);

    if (failedCount === 0) {
      addToast({
        type: 'success',
        title: t('toast.agentBulkUploadComplete.title'),
        description: summary || t('toast.agentBulkUploadComplete.description'),
      });
    } else {
      const allFailed = failedCount >= total;
      const base = t(
        allFailed ? 'toast.agentBulkUploadAllFailed.description' : 'toast.agentBulkUploadPartial.description',
        {
          uploaded: total,
          failed: failedCount,
        },
      );

      const failedRowCount = store.failedRows.length;
      const primaryAction = failedRowCount > 0
        ? {
            label: t('toast.agentBulkUploadReviewFailedRows', { count: failedRowCount }),
            onClick: () => store.requestReopen(),
          }
        : undefined;

      addToast({
        type: allFailed ? 'error' : 'warning',
        title: t(allFailed ? 'toast.agentBulkUploadAllFailed.title' : 'toast.agentBulkUploadPartial.title'),
        description: summary ? `${base}, ${summary}` : base,
        ...(primaryAction && { primaryAction }),
      });
    }

    queryClient.invalidateQueries({ queryKey: getAgentsQueryKey() });
  } catch (error) {
    handleError(error);
  }
};

export const handleAgentBulkCreateFailed = async (event: EventAgentsBulkCreateFailed): Promise<void> => {
  try {
    const enabled = await checkLDFlags([FeatureFlags.AGENT_CSV_UPLOAD_ENABLED]);
    if (!enabled) return;

    const description = event.data.message
      ? t('toast.agentBulkUploadFailed.descriptionWithReason', { reason: event.data.message })
      : t('toast.agentBulkUploadFailed.description');

    addToast({
      type: 'error',
      title: t('toast.agentBulkUploadFailed.title'),
      description,
    });
  } catch (error) {
    handleError(error);
  }
};

export type AgentsEventHandlers = {
  [K in AgentsEvent['eventKey']]: (event: Extract<AgentsEvent, { eventKey: K }>) => void;
};

export const agentsEventHandlers: AgentsEventHandlers = {
  'agents.bulk.create.complete': handleAgentBulkCreateComplete,
  'agents.bulk.create.failed': handleAgentBulkCreateFailed,
};
